import { ChangeEvent, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { AddAPhotoSharp, Cancel, Check, Delete, LocalShipping, PlayArrow, QrCodeScanner, Search as SearchIcon } from "@mui/icons-material";
import { Box, Chip, CircularProgress, IconButton, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@mui/material";
import moment from "moment";
import Layout from "Components/Layout/Layout";
import PaperTable from "Components/PaperTable";
import TextBlock from "Components/Layout/TextBlock";
import Plate from "Components/Plate/Plate";
import Actions from "Components/Actions";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarButton from "Components/Sidebar/SidebarButton";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import VehicleSearch from "Components/VehicleSearch";
import VehiclesNavigation from "Navigation/VehiclesNavigation";
import dateFormats from "system/dateFormats";
import { CollectionSessionElement, SessionDocumentType, Vehicle } from "system/Domain";
import CollectionSessionDocuments, { sessionDocumentTypes } from "./CollectionSessionDocuments";
import useCollectionSession from "./useCollectionSession";
import shipmentFormPreDeliveryValid from "./shipmentFormPreDeliveryValid";

const isElementReadyForPickup = (element: CollectionSessionElement) => {
	const forms = element.sale.shipping?.shipmentForms;
	return shipmentFormPreDeliveryValid(forms?.[forms.length - 1]);
};

const CollectionSessionPage = () => {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const [showSearch, setShowSearch] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const documentTypeRef = useRef<SessionDocumentType>("BillOfLading");

	const { session, elements, isLoading, addVehicle, removeVehicle, cancelSession, confirmShipping, uploadDocument, deleteDocument } = useCollectionSession(id);

	const handleVehiclePicked = async (vehicle: Vehicle) => {
		const added = await addVehicle({ vin: vehicle.vin });
		if (added) {
			setShowSearch(false);
		}
	};

	const isOpen = session?.status === "Created";
	const isCompleted = session?.status === "Completed";
	const activeElements = elements?.filter(e => !e.member.dateRemoved) ?? [];
	const allReadyForPickup = activeElements.length > 0 && activeElements.every(isElementReadyForPickup);
	const allShipped = activeElements.length > 0 && activeElements.every(e => e.sale.status === "Shipped");

	const startDocumentUpload = (type: SessionDocumentType) => () => {
		documentTypeRef.current = type;
		fileInputRef.current?.click();
	};

	const handleDocumentFile = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = "";

		if (file) {
			await uploadDocument(documentTypeRef.current, file);
		}
	};

	const startWorkSession = () => {
		const target = activeElements.find(e => !isElementReadyForPickup(e)) ?? activeElements[0];
		if (!target) return;

		history.push(`/vehicles/collection-sessions/${id}/work/${target.sale.id}`);
	};

	return (
		<Layout
			title="Sammelsitzung Ausgangsprotokolle"
			navigation={<VehiclesNavigation />}
			loading={!session}
			sidebar={
				<Sidebar>
					<SidebarGroup>
						<SidebarButton
							startIcon={<QrCodeScanner />}
							color="primary"
							label="Fahrzeug scannen"
							disabled={!isOpen}
							onClick={() => history.push(`/vehicles/collection-sessions/${id}/scan`)}
						/>
						<SidebarButton
							startIcon={<SearchIcon />}
							color="primary"
							label="Fahrzeug suchen"
							disabled={!isOpen}
							onClick={() => setShowSearch(s => !s)}
						/>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarButton
							startIcon={<PlayArrow />}
							color="primary"
							label="Protokolle ausfüllen"
							disabled={!isOpen || activeElements.length === 0}
							onClick={startWorkSession}
						/>
						<SidebarButton
							startIcon={<Check />}
							color="primary"
							label="Abholung abschließen"
							disabled={!isOpen || !allReadyForPickup}
							onClick={() => history.push(`/vehicles/collection-sessions/${id}/finalize`)}
						/>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarButton
							startIcon={<LocalShipping />}
							color="primary"
							label="Auslieferung bestätigen"
							disabled={!isCompleted || allShipped}
							onClick={confirmShipping}
						/>
					</SidebarGroup>
					<SidebarGroup>
						{(Object.keys(sessionDocumentTypes) as SessionDocumentType[]).map(type => (
							<SidebarButton
								key={type}
								startIcon={<AddAPhotoSharp />}
								color="primary"
								label={`${sessionDocumentTypes[type]} hinzufügen`}
								disabled={session?.status === "Cancelled" || isLoading}
								onClick={startDocumentUpload(type)}
							/>
						))}
					</SidebarGroup>
					<SidebarGroup>
						<SidebarButton
							startIcon={<Cancel />}
							color="inherit"
							label="Sitzung abbrechen"
							disabled={!isOpen}
							onClick={cancelSession}
						/>
					</SidebarGroup>
				</Sidebar>
			}
		>
			<input ref={fileInputRef} type="file" hidden capture onChange={handleDocumentFile} />
			{session && (
				<Box mb={2}>
					<Actions>
						<Chip
							label={
								session.status === "Created" ? "Offen"
									: session.status === "Completed" ? "Abgeschlossen"
										: "Abgebrochen"
							}
							color={session.status === "Created" ? "warning" : session.status === "Completed" ? "success" : "default"}
						/>
						<TextBlock
							primary={`Erstellt am ${moment(session.dateCreated).format(dateFormats.dateTime)}`}
							secondary={session.createdBy?.name}
						/>
					</Actions>
				</Box>
			)}

			{isLoading && (
				<Box m={2}>
					<CircularProgress size={32} />
				</Box>
			)}

			{showSearch && (
				<Box mb={2}>
					<VehicleSearch
						perespective="Stock"
						onClick={handleVehiclePicked}
						placeholder="FIN, Kennzeichen oder Volltext..."
						filter={v => !!v.remarketing?.saleId}
					/>
				</Box>
			)}

			{!elements && (
				<Box m={2}>
					<CircularProgress size={48} />
				</Box>
			)}

			{elements && activeElements.length === 0 && (
				<Box>Noch keine Fahrzeuge in dieser Sitzung</Box>
			)}

			{activeElements.length > 0 && (
				<PaperTable>
					<TableHead>
						<TableRow>
							<TableCell>Fahrzeug</TableCell>
							<TableCell>Standort</TableCell>
							<TableCell>Hinzugefügt</TableCell>
							<TableCell>Protokoll (Schritt 1-7)</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{activeElements.map(e => (
							<TableRow key={e.member.saleId}>
								<TableCell>
									<TextBlock
										primary={<Plate plateNumber={e.sale.plateNumber} />}
										secondary={e.sale.vin}
										tertiary={e.sale.vehicle?.type?.description}
									/>
								</TableCell>
								<TableCell>
									{e.compound?.name}
								</TableCell>
								<TableCell>
									<TextBlock
										primary={moment(e.member.dateAdded).format(dateFormats.dateTime)}
										secondary={e.member.addedBy?.name}
									/>
								</TableCell>
								<TableCell>
									<Chip
										size="small"
										label={e.sale.status === "Shipped" ? "Ausgeliefert" : isElementReadyForPickup(e) ? "Fertig" : "Offen"}
										color={e.sale.status === "Shipped" ? "info" : isElementReadyForPickup(e) ? "success" : "warning"}
										onClick={
											e.sale.status === "Shipped"
												? () => history.push(`/vehicles/${e.sale.vehicle.id}/after-shipping`)
												: isElementReadyForPickup(e)
													? () => history.push(`/vehicles/ship?saleId=${e.sale.id}`)
													: isOpen ? () => history.push(`/vehicles/collection-sessions/${id}/work/${e.sale.id}`) : undefined
										}
									/>
								</TableCell>
								<TableCell align="right">
									{isOpen && (
										<Tooltip title="Entfernen">
											<IconButton onClick={() => removeVehicle(e.sale.id)}>
												<Delete />
											</IconButton>
										</Tooltip>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</PaperTable>
			)}

			{session && session.status !== "Cancelled" && (
				<CollectionSessionDocuments
					session={session}
					canEdit={!isLoading}
					onDelete={deleteDocument}
				/>
			)}
		</Layout>
	);
};

export default CollectionSessionPage;
